import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiArrowLeft, FiAlertCircle, FiAward } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

// Hardcoded config fallback for standalone component
const config = {
  API_URL: "https://iflekt.com/Backend/index.php/FrontEndAPI",
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
  },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ mastery: [], misconceptions: [] });
  const [loading, setLoading] = useState(true);
  const email = sessionStorage.getItem("email");

  useEffect(() => {
    if (!email) {
      navigate("/SignIn");
      return;
    }

    const fetchData = async () => {
      try {
        const response = await axios.post(`${config.API_URL}/get_user_progress`, { email }, { headers: config.headers });
        if (response.data) {
          // If the API returns a string due to PHP warnings, try parsing it
          let parsedData = response.data;
          if (typeof parsedData === 'string') {
            try {
              // Sometimes PHP output might have text before the JSON
              const jsonStart = parsedData.indexOf('{');
              if (jsonStart !== -1) {
                parsedData = JSON.parse(parsedData.substring(jsonStart));
              }
            } catch (e) {
              console.error("Could not parse string data", e);
            }
          }

          setData({
            mastery: Array.isArray(parsedData?.mastery) ? parsedData.mastery : [],
            misconceptions: Array.isArray(parsedData?.misconceptions) ? parsedData.misconceptions : []
          });
        }
      } catch (err) {
        console.error("Failed to fetch progress", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [email, navigate]);

  return (
    <div className="flex h-full w-full flex-col bg-background p-6 md:p-12 overflow-y-auto text-foreground">
      <header className="mb-10 flex items-center justify-center border-b border-border pb-6 relative">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-0 flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-card-foreground shadow-sm transition-all hover:bg-muted z-10"
        >
          <FiArrowLeft size={16} /> Back to Chat
        </button>
        <h1 className="text-3xl font-bold tracking-tight text-foreground text-center w-full">Learning Analytics</h1>
      </header>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
            <p className="text-sm font-medium text-muted-foreground animate-pulse">Analyzing your progress...</p>
          </div>
        </div>
      ) : (
        <div className="mx-auto w-full max-w-7xl flex flex-col md:flex-row gap-8 md:gap-16 lg:gap-24 pb-12 px-4 md:px-0">
          
          {/* Topic Mastery Section */}
          <section className="flex-1 flex flex-col h-full">
            <div className="mb-6 flex items-center gap-3 shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <FiAward size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground">Topic Mastery</h2>
                <p className="text-sm text-muted-foreground">Your understanding of business concepts</p>
              </div>
            </div>

            {!data?.mastery || data.mastery.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-12 text-center shadow-sm min-h-[300px]">
                <FiAward className="mb-4 text-muted-foreground opacity-50" size={48} />
                <h3 className="mb-2 text-lg font-semibold text-foreground">No Mastery Data Yet</h3>
                <p className="max-w-md text-muted-foreground">
                  Start a conversation about a business topic. The AI will automatically assess your mastery as you chat!
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {data.mastery.map((item, idx) => (
                  <div key={idx} className="group flex flex-col xl:flex-row xl:items-center gap-6 rounded-2xl border border-border bg-card p-8 shadow-sm transition-all hover:shadow-md">
                    <h3 className="font-bold text-card-foreground text-xl tracking-tight xl:w-1/3 shrink-0 flex items-center">{item.topic_name}</h3>
                    
                    <div className="flex-1 flex flex-col sm:flex-row gap-8 w-full">
                      <div className="flex-1">
                        <div className="mb-1.5 flex justify-between text-sm font-medium">
                          <span className="text-muted-foreground">Mastery</span>
                          <span className="text-primary">{item.mastery_score}%</span>
                        </div>
                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted shadow-inner">
                          <div
                            className="h-full rounded-full bg-primary transition-all duration-1000 ease-out"
                            style={{ width: `${item.mastery_score}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="mb-1.5 flex justify-between text-sm font-medium">
                          <span className="text-muted-foreground">Confidence</span>
                          <span className="text-secondary-foreground opacity-80">{item.confidence_score}%</span>
                        </div>
                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted shadow-inner">
                          <div
                            className="h-full rounded-full bg-secondary transition-all duration-1000 ease-out"
                            style={{ width: `${item.confidence_score}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Misconceptions Section */}
          <section className="flex-1 flex flex-col h-full">
            <div className="mb-6 flex items-center gap-3 shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
                <FiAlertCircle size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground">Areas for Improvement</h2>
                <p className="text-sm text-muted-foreground">Conceptual misunderstandings to review</p>
              </div>
            </div>

            {!data?.misconceptions || data.misconceptions.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-12 text-center shadow-sm min-h-[300px]">
                <FiAlertCircle className="mb-4 text-muted-foreground opacity-50" size={48} />
                <h3 className="mb-2 text-lg font-semibold text-foreground">All Clear!</h3>
                <p className="max-w-md text-muted-foreground">
                  We haven't detected any misconceptions in your recent conversations. Keep up the great work!
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden h-fit">
                <ul className="divide-y divide-border">
                  {data.misconceptions.map((misc, idx) => (
                    <li key={idx} className="flex flex-col sm:flex-row sm:items-center gap-5 p-6 transition-colors hover:bg-muted/50">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive font-bold border border-destructive/20 shadow-sm">
                        {misc.frequency}x
                      </div>
                      <div className="flex-1">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="rounded-md bg-muted px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {misc.topic_name}
                          </span>
                        </div>
                        <p className="text-card-foreground leading-relaxed font-medium">{misc.misconception}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
